// utils/errorReporter.js
//
// Sends raw error detail to the server-side error log. The user never sees any
// of this — they get the plain sentence from errorMessages.js.
//
// ── PLACEHOLDER ─────────────────────────────────────────────────────────────
// The log service does not exist yet. Until it does, reports are queued locally
// and echoed to the console. To go live, set REPORTING_ENABLED = true; the
// payload already matches ERROR_HANDLING_SPEC.md §6, and anything queued in the
// meantime flushes on the next attempt.
// ────────────────────────────────────────────────────────────────────────────
//
// Four things this has to get right (spec §7), each of which has sunk a real
// error-reporting system:
//
//   1. The errors we most want CANNOT report themselves. A connectivity failure
//      means the report cannot be delivered either — the exact case that started
//      this. So reports queue and flush when the connection returns.
//   2. A broken client can flood the table. One user on a flapping connection
//      produces an error per request per retry, so identical failures collapse
//      into one entry with a count.
//   3. Logging must never cause an error. Every path here swallows failure.
//      A reporter that throws into a catch block turns one bug into two.
//   4. Raw text leaks PII and tokens. Auth keys, passwords and access codes get
//      scrubbed before anything leaves the browser.

const QUEUE_KEY = 'firesync-error-queue';
const MAX_QUEUE = 50;          // hard cap, oldest dropped first
const DEDUPE_WINDOW_MS = 60_000;
const MAX_RAW_LENGTH = 1024;   // matches RawMessage NVARCHAR(1024)
const MAX_STACK_LENGTH = 4000; // matches Stack NVARCHAR(4000)

/** Flip to true once the endpoint exists. That is the whole go-live change. */
// Turned on 2 Sep 2026 (owner), the same day production consoles went quiet
// (§C28): this queue is now the only record of client-side failures. The route,
// ErrorLogController.LogClientError, has been in the deployed Portal API since
// 7 Aug; every field the entry carries is a declared, compatible property on
// ClientErrorReport (see errorReporter.wire.test.js, which reads the C# model).
export const REPORTING_ENABLED = true;

export const ERROR_LOG_ENDPOINT = '/api/portal/errorlog/LogClientError';

/**
 * The version stamped on every report (spec §10.6).
 *
 * Re-exported, not redefined: it now lives in `utils/appVersion` alongside the
 * label the login screen shows. Those were separate values that disagreed
 * ('11.8' here vs "Version 1.0" on screen), which would have tagged beta reports
 * with a version no user ever saw.
 */
export { APP_VERSION } from '@/utils/appVersion';
import { APP_VERSION } from '@/utils/appVersion';

// ── Scrubbing ───────────────────────────────────────────────────────────────
// Anything resembling a credential is replaced before it leaves the browser.
// Deliberately aggressive: a redacted log entry is a minor loss, a leaked auth
// key in a database is not.
const SECRET_PATTERNS = [
    /([?&](?:key|token|sectoken|password|pw|access[_-]?code)=)[^&\s]+/gi,
    /("?(?:key|token|secToken|password|pw|accessCode)"?\s*[:=]\s*"?)[^",}\s]+/gi,
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, // GUID-shaped keys
];

export function scrub(text) {
    if (!text) return text;
    let out = String(text);
    for (const re of SECRET_PATTERNS) {
        out = out.replace(re, (match, prefix) => (prefix ? `${prefix}[redacted]` : '[redacted]'));
    }
    return out;
}

function truncate(text, max) {
    if (!text) return text;
    const s = String(text);
    return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

/**
 * A stable identity for "the same failure", so repeats collapse instead of
 * filling the table. Numbers and ids are stripped so that, say, five different
 * missing incident ids group as one problem rather than five.
 */
export function fingerprint({ category, endpoint, rawMessage, errorCode }) {
    const normalized = String(rawMessage || '')
        .toLowerCase()
        .replace(/[0-9a-f]{8,}/g, '#')   // ids, guids, hashes
        .replace(/\d+/g, '#')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 80);
    return [category || 'Unknown', endpoint || '-', errorCode ?? '-', normalized].join('|');
}

// ── Queue ───────────────────────────────────────────────────────────────────

function readQueue() {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(QUEUE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeQueue(queue) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
    } catch {
        // Storage full or unavailable — dropping a diagnostic is acceptable;
        // breaking the page because we could not log is not.
    }
}

export function queuedCount() {
    return readQueue().length;
}

export function clearQueue() {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(QUEUE_KEY); } catch { /* ignore */ }
}

// ── Reporting ───────────────────────────────────────────────────────────────

/**
 * Record an error for the server-side log. Never throws, never shows anything.
 *
 * @param {object} details
 * @param {string} details.category      Transport | Application | Client | Unknown
 * @param {string} details.userMessage   what the user was shown
 * @param {string} [details.rawMessage]
 * @param {number} [details.errorCode]
 * @param {number} [details.httpStatus]
 * @param {Error}  [details.error]
 * @param {string} [details.endpoint]
 * @param {string} [details.method]
 * @param {string} [details.route]       portal route the user was on
 * @param {string} [details.action]      what was being attempted
 * @param {string} [details.orgId]
 * @param {string} [details.userId]
 * @param {string} [details.authKey]     used for the request, never stored
 */
export function reportError(details = {}) {
    try {
        const {
            category, userMessage, rawMessage, errorCode, httpStatus, error,
            endpoint, method, route, action, orgId, userId,
        } = details;

        // Scrub ONCE, up front, and use only the scrubbed values from here on —
        // including for the fingerprint. Scrubbing the visible fields but then
        // deriving the fingerprint from the originals leaks the credential into
        // a field nobody thinks to check. (Caught by test, not by review.)
        const safeEndpoint = scrub(endpoint);
        const safeRaw = scrub(rawMessage || error?.message);

        const entry = {
            Category: category || 'Unknown',
            UserMessage: truncate(userMessage, 256),
            RawMessage: truncate(safeRaw, MAX_RAW_LENGTH),
            ErrorCode: typeof errorCode === 'number' ? errorCode : null,
            HttpStatus: typeof httpStatus === 'number' ? httpStatus : null,
            Stack: truncate(scrub(error?.stack), MAX_STACK_LENGTH),

            Endpoint: truncate(safeEndpoint, 256),
            Method: method || null,
            Route: truncate(route, 256),
            Action: truncate(action, 128),

            UserId: userId || null,
            OccurredUtc: new Date().toISOString(),
            AppVersion: APP_VERSION,
            UserAgent: typeof navigator !== 'undefined' ? truncate(navigator.userAgent, 512) : null,
            Online: typeof navigator !== 'undefined' ? navigator.onLine : null,

            Fingerprint: fingerprint({ category, endpoint: safeEndpoint, rawMessage: safeRaw, errorCode }),
            OccurrenceCount: 1,
        };

        const queue = readQueue();
        const now = Date.now();

        // Collapse a repeat of the same failure seen recently.
        const recent = queue.find(q =>
            q.Fingerprint === entry.Fingerprint &&
            now - new Date(q.OccurredUtc).getTime() < DEDUPE_WINDOW_MS);

        if (recent) {
            recent.OccurrenceCount = (recent.OccurrenceCount || 1) + 1;
            recent.OccurredUtc = entry.OccurredUtc;
        } else {
            queue.push({ ...entry, _orgId: orgId || null });
        }

        writeQueue(queue);

        if (!REPORTING_ENABLED) {
            // Placeholder mode: visible in dev, invisible to users.
            // Development only: the queue is a dev signal, and in production a
            // user's console should stay quiet even for a fingerprint.
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[errorReporter] queued (service not live yet):', entry.Fingerprint);
            }
            return;
        }

        flush(details.authKey, orgId);
    } catch {
        // Rule 3: reporting must never become a second failure.
    }
}

/**
 * Try to deliver everything queued. Safe to call often; a failure just leaves
 * the queue intact for next time.
 */
export async function flush(authKey = null, orgId = null) {
    if (!REPORTING_ENABLED) return;
    if (typeof window === 'undefined') return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const queue = readQueue();
    if (queue.length === 0) return;

    const remaining = [];
    for (const entry of queue) {
        const { _orgId, ...error } = entry;
        try {
            const res = await fetch(ERROR_LOG_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Key: authKey || null,
                    OrgId: _orgId || orgId || null,
                    Error: error,
                }),
            });
            // Any answer at all means it arrived; the response is ignored on
            // purpose so a logging failure can never surface to the user.
            if (!res.ok && res.status >= 500) remaining.push(entry);
        } catch {
            remaining.push(entry); // still offline — keep for next time
        }
    }

    writeQueue(remaining);
}

/**
 * Flush whenever connectivity comes back. This is what makes transport errors
 * reportable at all — they are created precisely when delivery is impossible.
 */
export function installFlushOnReconnect(getAuth = () => ({})) {
    if (typeof window === 'undefined') return () => {};
    const handler = () => {
        const { authKey, orgId } = getAuth() || {};
        flush(authKey, orgId);
    };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
}
